import { useContext, useEffect, useRef, useState } from 'react';
import { getImageSrc } from '@/common/helios/OrderStatusCard/utils';
import TrackWrapper from '@/common/TrackWrapper';
import SectionWrapper from '@/home/common/SectionWrapper';
import { FinanceCard, TradeCard } from '@/home/ReturnUser/HeroCards/Cards';
import {
  FinanceTradeWrapper,
  GridWrapper,
  HeroFavoriteAndSearchCard
} from '@/home/ReturnUser/HeroCards/HeroCards.styles';
import SRPLinks from '@/home/ReturnUser/HeroCards/LinkCards/SRPLinks';
import VDPLinks from '@/home/ReturnUser/HeroCards/LinkCards/VDPLinks';
import { useConsumer } from '~/context/common/StaticContext';
import { useGetUser } from '~/context/common/UserContext';
import UserPurchaseContext from '~/context/home/UserPurchaseContext';
import defaultImageHandler from '~/utilities/defaultImageHandler';
import formatEvent from '~/utilities/formatEvent';
import KeepShoppingBand from './KeepShoppingBand';
import OrderStatusCard from './OrderStatusCard';
import {
  computeEllipseLeft,
  HERO_HEIGHT,
  MOBILE_MASK,
  TABLET_MASK
} from './OrderStatusModule.masks';
import { DesktopEllipse } from './OrderStatusModule.styles';
import { getOrderStatusCardConfig } from './utils';

export default function OrderStatusModule() {
  const {
    userPurchase: {
      data: { openVerifications, orderState, vehicle }
    }
  } = useContext(UserPurchaseContext);
  const consumer = useConsumer();
  const user = useGetUser();
  const desktopHeroRef = useRef<HTMLDivElement>(null);
  const [ellipseLeft, setEllipseLeft] = useState(-738);

  useEffect(() => {
    const el = desktopHeroRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() =>
      setEllipseLeft(computeEllipseLeft(el.clientWidth))
    );
    ro.observe(el);
    setEllipseLeft(computeEllipseLeft(el.clientWidth));

    return () => ro.disconnect();
  }, []);

  if (!vehicle) return null;

  const { imageUrl, inventoryType, make, stockNumber, vehicleId } = vehicle;

  const givenName = user?.given_name ?? '';

  const config = getOrderStatusCardConfig({
    consumer,
    givenName,
    make,
    openVerifications,
    orderState,
    vehicleId
  });

  const heroImageSrc = getImageSrc({
    imageUrl,
    imageWidth: 900,
    inventoryType,
    stockNumber
  });

  const cardProps = { config, orderState, vehicle };

  return (
    <div className="flex w-full flex-col bg-background-default">
      <TrackWrapper
        event={formatEvent('Home feed card', {
          action: 'viewed',
          description: 'Order Status Card',
          status: orderState,
          vehicleId
        })}
      >
        {/* ── Mobile (<768): full-bleed studio image with curved bottom arc ── */}
        <div className="sm:hidden">
          <img
            alt=""
            aria-hidden="true"
            className="block h-[210px] w-full select-none object-cover"
            onError={defaultImageHandler}
            src={heroImageSrc}
            style={MOBILE_MASK}
          />
          <div className="mx-auto w-full max-w-[1352px] p-2xl">
            <OrderStatusCard {...cardProps} />
          </div>
        </div>

        {/* ── Tablet (768–1023): ellipse reveal, card overlays left zone ── */}
        <div
          className="relative hidden w-full overflow-hidden bg-background-default sm:block md:hidden"
          style={{ minHeight: HERO_HEIGHT }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 select-none"
            style={TABLET_MASK}
          >
            <img
              alt=""
              className="absolute h-auto w-[800px] max-w-none"
              onError={defaultImageHandler}
              src={heroImageSrc}
              style={{ left: 200, top: '55%', transform: 'translateY(-50%)' }}
            />
          </div>
          <div
            className="relative flex w-full flex-col justify-center py-40"
            style={{ minHeight: HERO_HEIGHT }}
          >
            <div className="mx-auto w-full max-w-[1352px] px-3xl">
              <div className="max-w-[420px]">
                <OrderStatusCard {...cardProps} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Desktop (≥1024): column-anchored ellipse, car upright ── */}
        <div
          ref={desktopHeroRef}
          className="relative hidden w-full overflow-hidden bg-background-default md:block"
          style={{ height: HERO_HEIGHT }}
        >
          <div className="relative mx-auto h-full w-full max-w-[1352px]">
            <div className="relative z-[2] flex h-full max-w-[735px] flex-col justify-center pl-4xl">
              <OrderStatusCard {...cardProps} />
            </div>
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-full z-[1] w-0"
            >
              <DesktopEllipse
                style={{
                  backgroundImage: `url(${heroImageSrc})`,
                  left: ellipseLeft
                }}
              />
            </div>
          </div>
        </div>
      </TrackWrapper>

      <KeepShoppingBand />

      <SectionWrapper
        analyticsSection="Hero Cards"
        className="pt-24 sm:pt-4xl"
        hasSideSpacing
        hasBottomSpacing
      >
        <GridWrapper>
          <HeroFavoriteAndSearchCard>
            <VDPLinks />
          </HeroFavoriteAndSearchCard>
          <HeroFavoriteAndSearchCard>
            <SRPLinks />
          </HeroFavoriteAndSearchCard>
          <FinanceTradeWrapper>
            <FinanceCard />
            <TradeCard />
          </FinanceTradeWrapper>
        </GridWrapper>
      </SectionWrapper>
    </div>
  );
}
